import angular from "angular";

import { AnnualLeavePayment, AnnualLeavePaymentKind, Dates, HolidaysLogic, HolidayYear, LegalEntityType, Avatar, HolidayCode, HolidayBonusPaymentMethod } from "@salaxy/core";

import { UiHelpers, ReportsService } from "../../services";

import { ListControllerBase, ListControllerBaseBindings } from "../bases";

type AnnualLeavePaymentWithAvatar = AnnualLeavePayment & { avatar: Avatar };
/**
 * Controls the annual leave payments: HolidayCompensation, HolidayBonus and HolidaySalary.
 * These payments are typically fetched from paid calculations automatically,
 * but may also be marked paid manually. Also, in client-side logic, payments are fetched
 * optionally from Draft calculations.
 */
export class HolidayYearPaidController  extends ListControllerBase<HolidayYear, AnnualLeavePayment> {

  /** Bindings for components that use this controller */
  public static bindings = new ListControllerBaseBindings();

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "UiHelpers",
    "ReportsService"
  ];

  /** Total holiday pays */
  public totals: {
    /** Total calculation  */
    total: AnnualLeavePayment,
    /** Start saldo of the payments */
    startSaldo: AnnualLeavePayment,

    /** End saldo of the payments. */
    endSaldo: AnnualLeavePayment,
  };

  /** Total bonus */
  public bonusTotals: {
    /** Total calculation  */
    total: AnnualLeavePayment,
    /** Start saldo of the payments */
    startSaldo: AnnualLeavePayment,

    /** End saldo of the payments. */
    endSaldo: AnnualLeavePayment,
  };

  constructor(
    uiHelpers: UiHelpers,
    private reports: ReportsService,
  ) {
    super(uiHelpers);
  }

  /** Gets the worker ID for the current holiday year. */
  public get workerId(): string {
    if (!this.parent || !this.parent.workerSnapshot) {
      return null;
    }
    return this.parent.workerSnapshot.id;
  }

  /** Gets the employment ID for the current holiday year. */
  public get employmentId(): string {
    if (!this.parent || !this.parent.workerSnapshot) {
      return null;
    }
    return this.parent.employmentId;
  }

  /**
   * List of items fetched from the holiday year,
   * cached to the controller level, except when workerId changes.
   */
  public get list(): AnnualLeavePayment[] {
    if (!this.parent || !this.parent.leaves || !this.parent.leaves.paid) {
      return [];
    }
    return this.getLeavesPaid();
  }

  /**
   * Returns true if the bonus should be paid for the employee.
   */
  public get shoudPayBonus(): boolean {
    if (!this.parent?.spec) {
      return false;
    }

    return ( this.parent.spec.code === HolidayCode.Permanent14Days
          || this.parent.spec.code  === HolidayCode.Permanent35Hours
          || this.parent.spec.code  === HolidayCode.TemporaryTimeOff
          || this.parent.spec.code  === HolidayCode.Other
          || this.parent.spec.code  === HolidayCode.Script
    ) && this.parent.spec.bonusMethod !== HolidayBonusPaymentMethod.None;
  }

/**
 * List of paid holidays.
 */
  private getLeavesPaid(): AnnualLeavePayment[] {
      for (const paidLeave of this.parent.leaves.paid) {
          (paidLeave as any).avatar ??= {};
          if (paidLeave.holidayBonus > 0 && !(paidLeave.holidaySalary > 0 || paidLeave.holidayCompensation > 0)) {
            angular.copy(HolidaysLogic.getPaidBonusAvatar("row", paidLeave), (paidLeave as any).avatar);
          }
          else
          {
            angular.copy(HolidaysLogic.getPaidCalculationAvatar("row", paidLeave), (paidLeave as any).avatar);
          }
      }

      this.totals ??= {} as any;
      this.totals.startSaldo = this.copyAnnualLeavePayment(HolidaysLogic.getPaidHolidaysCalculation(this.parent, "startSaldo"), this.totals.startSaldo);
      this.totals.total = this.copyAnnualLeavePayment(HolidaysLogic.getPaidHolidaysCalculation(this.parent, "total"), this.totals.total);
      this.totals.endSaldo = this.copyAnnualLeavePayment(HolidaysLogic.getPaidHolidaysCalculation(this.parent, "endSaldo"), this.totals.endSaldo);

      this.bonusTotals ??= {} as any;
      this.bonusTotals.startSaldo = this.copyAnnualLeavePayment(HolidaysLogic.getPaidBonusCalculation(this.parent, "startSaldo"), this.bonusTotals.startSaldo);
      this.bonusTotals.total = this.copyAnnualLeavePayment(HolidaysLogic.getPaidBonusCalculation(this.parent, "total"), this.bonusTotals.total);
      this.bonusTotals.endSaldo = this.copyAnnualLeavePayment(HolidaysLogic.getPaidBonusCalculation(this.parent, "endSaldo"), this.bonusTotals.endSaldo);
      return this.parent.leaves.paid;
  }

  private copyAnnualLeavePayment(source: AnnualLeavePayment, target: AnnualLeavePayment):  AnnualLeavePayment{
    target ??= {} as any;
    (target as any).avatar ??= {} as any;

    angular.copy((source as any).avatar, (target as any).avatar);
    target.holidayBonus = source.holidayBonus;
    target.holidayCompensation = source.holidayCompensation;
    target.holidayDays = source.holidayDays;
    target.holidaySalary = source.holidaySalary;

    return target;
  }

  /** Creating of a new item. */
  public getBlank(): AnnualLeavePaymentWithAvatar {
    return {
      kind: AnnualLeavePaymentKind.Undefined,
      avatar: {
        displayName: "Käsin syötetty korjaus",
        description: null,
        color: "rgb(74, 146, 233)",
        initials: "edit",
        entityType: LegalEntityType.Company,
      },
      date: Dates.getToday(),
      period: Dates.getDateRange(Dates.getToday(), Dates.getToday()),
      holidayBonus: null,
      holidayCompensation: null,
      holidayDays: null,
      holidaySalary: null,
    };
  }

  /** Gets calculations preview pdf url based on given id */
  public getPdfUrl(calcId: string) {
    return this.reports.getPdfUrlForCalc("salarySlip", calcId, true);
  }

  /** Logic for edit dialog. */
  public getEditDialogLogic() {
    return {
      /** Updates the workdays count */
      updatePeriodDays: (current: AnnualLeavePayment) => {
        if (current.period.start > current.period.end) {
          current.period.end = current.period.start;
        }
        if (current.period.start && current.period.end) {
          if (current.period.days) {
            current.period.days = Dates.getVacationDays(current.period.start, current.period.end);
            current.period.daysCount = current.period.days.length;
          } else {
            current.period.daysCount = Dates.getVacationDays(current.period.start, current.period.end).length;
          }
          current.holidayDays = current.period.daysCount;
        }
      },
      updateDaysCount: (current: AnnualLeavePayment) => {
        current.period.daysCount = current.holidayDays;
      },
      getPdfUrl: (calcId: string): string => this.getPdfUrl(calcId)
    };
  }

  /** Template for edit UI that is shown in a modal dialog. */
  public getEditDialogTemplateUrl() {
    return "salaxy-components/worker/holidays/HolidayYearPaidEditDialog.html";
  }
}